import { create } from 'zustand'
import { sumMoney } from '@/lib/money'
import { User } from 'firebase/auth'
import { Unsubscribe } from 'firebase/firestore'
import { toast } from 'sonner'
import { 
  Earning, 
  CreateEarningData, 
  UpdateEarningData
} from '@/types/earnings'
import { EarningsService } from '@/services/earningsService'

interface EarningsState {
  // State
  earnings: Earning[]
  loading: boolean
  error: string | null
  
  // Subscription management
  unsubscribe: Unsubscribe | null
  
  // Actions
  loadAllEarnings: (user: User) => Promise<void>
  loadEarningsByDriver: (user: User, driverId: string) => Promise<void>
  loadEarningsByPeriod: (user: User, period: string) => Promise<void>
  createEarning: (user: User, data: CreateEarningData) => Promise<Earning>
  updateEarning: (user: User, id: string, updates: UpdateEarningData) => Promise<void>
  deleteEarning: (user: User, id: string) => Promise<void>
  clearError: () => void
  reset: () => void
  
  // Computed getters
  getEarningById: (id: string) => Earning | undefined
  getEarningsByDriver: (driverId: string) => Earning[]
  getEarningsByPeriod: (period: string) => Earning[]
  getEarningsByUpload: (uploadSessionId: string) => Earning[]
  
  // Cleanup
  cleanup: () => void
}

export const useEarningsStore = create<EarningsState>((set, get) => ({
  // Initial state
  earnings: [],
  loading: false,
  error: null,
  unsubscribe: null,

  // Load all earnings with real-time subscription
  loadAllEarnings: async (user: User) => {
    try {
      set({ loading: true, error: null })
      
      // Clean up existing subscription
      const { unsubscribe } = get()
      if (unsubscribe) {
        unsubscribe()
      }
      
      // Set up real-time subscription for all earnings
      const newUnsubscribe = EarningsService.subscribeToEarnings(user, (earnings) => {
        set({ earnings, loading: false, error: null })
      })
      
      set({ unsubscribe: newUnsubscribe })
      
    } catch (error) {
      console.error('Failed to load earnings:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ladda inkomster'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
    }
  },

  // Load earnings by driver with real-time subscription
  loadEarningsByDriver: async (user: User, driverId: string) => {
    try {
      set({ loading: true, error: null })
      
      const earnings = await EarningsService.getEarningsByDriver(user, driverId)
      set({ earnings, loading: false, error: null })
      
    } catch (error) {
      console.error('Failed to load earnings by driver:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ladda förarens inkomster'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
    }
  },

  // Load earnings by period with real-time subscription
  loadEarningsByPeriod: async (user: User, period: string) => {
    try {
      set({ loading: true, error: null })

      const earnings = await EarningsService.getEarningsByPeriod(user, period)
      set({ earnings, loading: false, error: null })
      
    } catch (error) {
      console.error('Failed to load earnings by period:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ladda periodens inkomster'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
    }
  },

  // Create new earning
  createEarning: async (user: User, data: CreateEarningData) => {
    try {
      set({ loading: true, error: null })
      
      const newEarning = await EarningsService.createEarning(user, data)
      
      // Add to local state
      const { earnings } = get()
      set({ 
        earnings: [newEarning, ...earnings], 
        loading: false 
      })
      
      toast.success('Inkomstpost har skapats')
      
      return newEarning
      
    } catch (error) {
      console.error('Failed to create earning:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att skapa inkomstposten'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Update existing earning
  updateEarning: async (user: User, id: string, updates: UpdateEarningData) => {
    try {
      set({ loading: true, error: null })
      
      const updatedEarning = await EarningsService.updateEarning(user, id, updates)
      
      // Update local state
      const { earnings } = get()
      const updatedEarnings = earnings.map(earning => 
        earning.id === id ? updatedEarning : earning
      )
      
      set({ 
        earnings: updatedEarnings, 
        loading: false 
      })
      
      toast.success('Inkomstpost har uppdaterats')
      
    } catch (error) {
      console.error('Failed to update earning:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att uppdatera inkomstposten'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Delete earning
  deleteEarning: async (user: User, id: string) => {
    try {
      const { earnings } = get()
      const earning = earnings.find(e => e.id === id)
      const earningDescription = earning ? `från ${earning.platform}` : 'Okänd inkomstpost'
      
      set({ loading: true, error: null })
      
      await EarningsService.deleteEarning(user, id)
      
      // Remove from local state
      const updatedEarnings = earnings.filter(earning => earning.id !== id)
      set({ 
        earnings: updatedEarnings, 
        loading: false 
      })
      
      toast.success(`Inkomstpost ${earningDescription} har tagits bort`)
      
    } catch (error) {
      console.error('Failed to delete earning:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ta bort inkomstposten'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Clear error state
  clearError: () => {
    set({ error: null })
  },

  // Reset store to initial state
  reset: () => {
    const { unsubscribe } = get()
    if (unsubscribe) {
      unsubscribe()
    }
    
    set({
      earnings: [],
      loading: false,
      error: null,
      unsubscribe: null
    })
  },

  // Get earning by ID
  getEarningById: (id: string) => {
    const { earnings } = get()
    return earnings.find(earning => earning.id === id)
  },

  // Get earnings by driver ID (from local state)
  getEarningsByDriver: (driverId: string) => {
    const { earnings } = get()
    return earnings.filter(earning => earning.driverId === driverId)
  },

  // Get earnings by period (from local state)
  getEarningsByPeriod: (period: string) => {
    const { earnings } = get()
    return earnings.filter(earning => earning.period === period)
  },

  // Get earnings by upload session ID (from local state)
  getEarningsByUpload: (uploadSessionId: string) => {
    const { earnings } = get()
    return earnings.filter(earning => earning.uploadSessionId === uploadSessionId)
  },

  // Cleanup subscriptions
  cleanup: () => {
    const { unsubscribe } = get()
    if (unsubscribe) {
      unsubscribe()
      set({ unsubscribe: null })
    }
  }
}))

// Export hook for easier usage with computed values
export const useEarnings = () => {
  const store = useEarningsStore()
  return {
    ...store,
    // Add any computed values here if needed
    totalGrossEarnings: sumMoney(store.earnings.map(earning => earning.grossEarnings)),
    totalNetEarnings: sumMoney(store.earnings.map(earning => earning.netEarnings)),
    totalTips: sumMoney(store.earnings.map(earning => earning.tips)),
  }
}